import { AfterViewInit, Directive, ElementRef, Input, OnDestroy } from '@angular/core';
import {
  SPLIT_ROW_BASIS_PROPERTY,
  SPLIT_ROW_DEFAULT_PERCENT,
  SPLIT_ROW_DRAGGING_CLASS,
  SPLIT_ROW_HANDLE_LEFT_PROPERTY,
  SPLIT_ROW_HANDLE_SELECTOR,
  SPLIT_ROW_KEY_STEP_PERCENT,
  SPLIT_ROW_MAX_PERCENT,
  SPLIT_ROW_MIN_PERCENT,
  SPLIT_ROW_PANE_SELECTOR,
  SPLIT_ROW_SINGLE_CLASS,
  SPLIT_ROW_STORAGE_PREFIX,
} from 'src/app/core/helpers/ui/split-row.constant';

@Directive({
  selector: '[SplitRow]',
})
export class SplitRowDirective implements AfterViewInit, OnDestroy {
  @Input('SplitRow') public storageKey: string = '';

  private readonly host: HTMLElement;

  private panes: HTMLElement[] = [];
  private handle: HTMLElement | null = null;
  private ratio: number = SPLIT_ROW_DEFAULT_PERCENT;
  private isDragging: boolean = false;
  private paneObserver?: MutationObserver;
  private paneMinPercents: [number, number] = [0, 0];

  constructor(elementRef: ElementRef<HTMLElement>) {
    this.host = elementRef.nativeElement;
  }

  ngAfterViewInit(): void {
    this.panes = Array.from(this.host.querySelectorAll<HTMLElement>(SPLIT_ROW_PANE_SELECTOR));
    this.handle = this.host.querySelector<HTMLElement>(SPLIT_ROW_HANDLE_SELECTOR);
    this.ratio = this.restore();

    this.bindHandle();
    this.observePanes();
    window.addEventListener('resize', this.onResize);
    this.measurePaneMinimums();
    this.apply();
  }

  ngOnDestroy(): void {
    this.paneObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    this.unbindHandle();
  }

  private readonly onResize = (): void => {
    this.measurePaneMinimums();
    this.apply();
  };

  private get populatedPanes(): HTMLElement[] {
    return this.panes.filter((pane) => pane.children.length > 0);
  }

  private observePanes(): void {
    this.paneObserver = new MutationObserver(() => {
      this.measurePaneMinimums();
      this.apply();
    });
    this.panes.forEach((pane) => this.paneObserver?.observe(pane, { childList: true }));
  }

  private bindHandle(): void {
    if (!this.handle) return;

    this.handle.addEventListener('pointerdown', this.onPointerDown);
    this.handle.addEventListener('pointermove', this.onPointerMove);
    this.handle.addEventListener('pointerup', this.onPointerRelease);
    this.handle.addEventListener('pointercancel', this.onPointerRelease);
    this.handle.addEventListener('dblclick', this.onDoubleClick);
    this.handle.addEventListener('keydown', this.onKeyDown);
  }

  private unbindHandle(): void {
    if (!this.handle) return;

    this.handle.removeEventListener('pointerdown', this.onPointerDown);
    this.handle.removeEventListener('pointermove', this.onPointerMove);
    this.handle.removeEventListener('pointerup', this.onPointerRelease);
    this.handle.removeEventListener('pointercancel', this.onPointerRelease);
    this.handle.removeEventListener('dblclick', this.onDoubleClick);
    this.handle.removeEventListener('keydown', this.onKeyDown);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.populatedPanes.length < 2) return;

    this.measurePaneMinimums();
    this.isDragging = true;
    this.host.classList.add(SPLIT_ROW_DRAGGING_CLASS);

    this.handle?.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.isDragging) return;

    const bounds = this.host.getBoundingClientRect();
    if (!bounds.width) return;

    this.setRatio(((event.clientX - bounds.left) / bounds.width) * 100);
  };

  private readonly onPointerRelease = (event: PointerEvent): void => {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.host.classList.remove(SPLIT_ROW_DRAGGING_CLASS);

    if (this.handle?.hasPointerCapture(event.pointerId)) {
      this.handle.releasePointerCapture(event.pointerId);
    }

    this.persist();
  };

  private readonly onDoubleClick = (): void => {
    if (this.populatedPanes.length < 2) return;

    this.setRatio(SPLIT_ROW_DEFAULT_PERCENT);
    this.persist();
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const step =
      event.key === 'ArrowLeft'
        ? -SPLIT_ROW_KEY_STEP_PERCENT
        : event.key === 'ArrowRight'
          ? SPLIT_ROW_KEY_STEP_PERCENT
          : 0;

    if (!step || this.populatedPanes.length < 2) return;

    this.setRatio(this.ratio + step);
    this.persist();
    event.preventDefault();
  };

  private setRatio(percent: number): void {
    this.ratio = percent;
    this.apply();
  }

  private measurePaneMinimums(): void {
    const width = this.host.getBoundingClientRect().width;
    const [first, second] = this.populatedPanes;

    this.paneMinPercents = [this.paneMinPercent(first, width), this.paneMinPercent(second, width)];
  }

  private paneMinPercent(pane: HTMLElement | undefined, hostWidth: number): number {
    if (!pane || !hostWidth) return 0;

    const minWidth = Number.parseFloat(getComputedStyle(pane).minWidth);
    if (!Number.isFinite(minWidth) || minWidth <= 0) return 0;

    return (minWidth / hostWidth) * 100;
  }

  private get bounds(): { min: number; max: number } {
    const [firstMin, secondMin] = this.paneMinPercents;

    const min = Math.max(SPLIT_ROW_MIN_PERCENT, firstMin);
    const max = Math.min(SPLIT_ROW_MAX_PERCENT, 100 - secondMin);

    return { min, max: Math.max(min, max) };
  }

  private apply(): void {
    const populated = this.populatedPanes;
    const isSplit = populated.length > 1;

    this.host.classList.toggle(SPLIT_ROW_SINGLE_CLASS, !isSplit);

    if (!isSplit) {
      this.panes.forEach((pane) =>
        pane.style.setProperty(SPLIT_ROW_BASIS_PROPERTY, pane.children.length ? '100%' : '0%')
      );
      return;
    }

    const [first, second] = populated;
    const { min, max } = this.bounds;
    this.ratio = Math.min(max, Math.max(min, this.ratio));

    first.style.setProperty(SPLIT_ROW_BASIS_PROPERTY, `${this.ratio}%`);
    second.style.setProperty(SPLIT_ROW_BASIS_PROPERTY, `${100 - this.ratio}%`);
    this.handle?.style.setProperty(SPLIT_ROW_HANDLE_LEFT_PROPERTY, `${this.ratio}%`);
    this.handle?.setAttribute('aria-valuenow', `${Math.round(this.ratio)}`);
    this.handle?.setAttribute('aria-valuemin', `${Math.round(min)}`);
    this.handle?.setAttribute('aria-valuemax', `${Math.round(max)}`);
  }

  private restore(): number {
    if (!this.storageKey) return SPLIT_ROW_DEFAULT_PERCENT;

    try {
      const stored = Number(localStorage.getItem(`${SPLIT_ROW_STORAGE_PREFIX}${this.storageKey}`));
      if (!Number.isFinite(stored) || stored <= 0) return SPLIT_ROW_DEFAULT_PERCENT;

      return Math.min(SPLIT_ROW_MAX_PERCENT, Math.max(SPLIT_ROW_MIN_PERCENT, stored));
    } catch {
      return SPLIT_ROW_DEFAULT_PERCENT;
    }
  }

  private persist(): void {
    if (!this.storageKey) return;

    try {
      localStorage.setItem(`${SPLIT_ROW_STORAGE_PREFIX}${this.storageKey}`, `${this.ratio}`);
    } catch {}
  }
}
